import React, { useEffect, useState } from "react";
import { FloatingActionButton } from "@/components/ui/floating-action-button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SFIcon } from "@/components/ui/sf-icon";
import { sfIcons } from "@/lib/sf-icons";
import type { DashboardState } from "@/hooks/useDashboardState";
import { useFabSuppression } from "@/hooks/useFabSuppression";

interface SmartFabDropdownProps {
  state: DashboardState;
  onAddBankAccount: () => void;
  onAddAddress: () => void;
  onCopyAddress?: () => void;
  latestAddress?: string;
}

export const SmartFabDropdown = ({ 
  state, 
  onAddBankAccount, 
  onAddAddress, 
  onCopyAddress,
  latestAddress 
}: SmartFabDropdownProps) => {
  const [open, setOpen] = useState(false);
  const suppressed = useFabSuppression();

  useEffect(() => {
    if (suppressed && open) {
      setOpen(false);
    }
  }, [suppressed, open]);

  // For non-ACTIVE states, show single action button
  if (state !== "ACTIVE") {
    const getConfig = () => {
      switch (state) {
        case "EMPTY":
          return {
            icon: <SFIcon icon={sfIcons.creditCard} size="lg" />,
            label: "Add Bank Account",
            onClick: onAddBankAccount,
          };
        case "BANK_ADDED":
          return {
            icon: <SFIcon icon={sfIcons.wallet} size="lg" />,
            label: "Create Address", 
            onClick: onAddAddress,
          };
        case "ADDRESS_CREATED":
          return {
            icon: <SFIcon icon={sfIcons.copy} size="lg" />,
            label: "Copy Address",
            onClick: onCopyAddress || (() => {
              if (latestAddress) {
                navigator.clipboard.writeText(latestAddress);
              }
            }),
          };
        default:
          return {
            icon: <SFIcon icon={sfIcons.add} size="lg" />,
            label: "Quick Action",
            onClick: () => {},
          };
      }
    };

    const config = getConfig();

    return (
      <FloatingActionButton
        onClick={config.onClick}
        className="bg-gradient-to-r from-primary to-primary-glow hover:opacity-90 text-primary-foreground"
        title={config.label}
        aria-label={config.label}
        data-suppressed={suppressed ? "true" : undefined}
        aria-hidden={suppressed || undefined}
        tabIndex={suppressed ? -1 : undefined}
      >
        {React.cloneElement(config.icon, { 'aria-hidden': 'true' })}
      </FloatingActionButton>
    );
  }

  // For ACTIVE state, show dropdown menu
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <FloatingActionButton
          className="bg-gradient-to-r from-primary to-primary-glow hover:shadow-md transition-shadow duration-120 data-[state=open]:rotate-45"
          title="Quick Actions"
          data-suppressed={suppressed ? "true" : undefined}
          aria-hidden={suppressed || undefined}
          tabIndex={suppressed ? -1 : undefined}
        >
          {open ? <SFIcon icon={sfIcons.chevronUp} size="lg" /> : <SFIcon icon={sfIcons.add} size="lg" />}
        </FloatingActionButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        side="top" 
        className="mb-2 w-64 bg-card border-border/50 backdrop-blur-sm z-50"
      >
        <DropdownMenuItem onClick={onAddAddress} className="gap-3 min-h-[48px] text-base sm:text-sm touch-manipulation cursor-pointer">
          <SFIcon icon={sfIcons.wallet} size="md" />
          Create New Address
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onAddBankAccount} className="gap-3 min-h-[48px] text-base sm:text-sm touch-manipulation cursor-pointer">
          <SFIcon icon={sfIcons.creditCard} size="md" />
          Add Bank Account
        </DropdownMenuItem>
        {latestAddress && (
          <DropdownMenuItem 
            onClick={() => {
              navigator.clipboard.writeText(latestAddress);
              setOpen(false);
            }}
            className="gap-3 min-h-[48px] text-base sm:text-sm touch-manipulation cursor-pointer"
          >
            <SFIcon icon={sfIcons.copy} size="md" />
            Copy Latest Address
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};